import React, { useEffect, useMemo } from 'react'

import AddVaultForm from '../components/vault/AddVaultForm'
import TransactionForm from '@/components/transactions/TransactionForm'
import useAppStore from '@/store/useAppStore'
import { NON_MODAL_WINDOW_TYPE } from '@/data/constants/constants'
import objectHash from 'object-hash'
import AddTransactionCategoryForm from '../components/transactionCategory/AddTransactionCategoryForm'
import EditTransactionCategoryForm from '../components/transactionCategory/EditTransactionCategoryForm'
import EditVaultForm from '../components/vault/EditVaultForm'
import EditGroupForm from '../components/nonModalWindows/EditGroup'
import { Cell } from '@tanstack/react-table'
import Models from '../components/model/Models'
import ModelsWindow from '../components/model/ModelsWindow'

export default function useOpenNonModalWindow() {
  const setNonModalWindowState = useAppStore(
    (state) => state.setNonModalWindowState,
  )
  const setNonModalProps = useAppStore((state) => state.setNonModalProps)
  const nonModalProps = useAppStore((state) => state.nonModalProps)
  const setNonModalWindowType = useAppStore(
    (state) => state.setNonModalWindowType,
  )
  const nonModalWindowType = useAppStore((state) => state.nonModalWindowType)
  const nonModalWindowState = useAppStore((state) => state.nonModalWindowState)

  const open = ({
    name,
    currentCell,
    rerender = undefined,
    ...params
  }: {
    name: string
    currentCell?: Cell<unknown, unknown>
    rerender?: boolean
  }) => {
    setNonModalWindowState('opened')
    setNonModalWindowType(name)

    const modalProps = { name, currentCell, ...params }
    const componentKey = rerender
      ? objectHash(modalProps)
      : nonModalProps?.componentKey ?? undefined

    setNonModalProps({ ...modalProps, componentKey })
  }

  const updateExistingWindow = ({ rerender, ...params }) => {
    const modalProps = { ...nonModalProps, ...params }
    const componentKey = rerender
      ? objectHash(modalProps)
      : nonModalProps?.componentKey ?? undefined

    setNonModalProps({ ...modalProps, componentKey })
  }

  useEffect(() => {
    if (nonModalWindowState === 'closed') {
      setNonModalProps({})
    }
  }, [nonModalWindowState, setNonModalWindowState])

  const PortalComponent = useMemo(() => {
    const componentMap = {
      [NON_MODAL_WINDOW_TYPE.TRANSACTION]: TransactionForm,
      [NON_MODAL_WINDOW_TYPE.VAULT]: AddVaultForm,
      [NON_MODAL_WINDOW_TYPE.TRANSACTION_CATEGORY_EXPENSE]:
        AddTransactionCategoryForm,
      [NON_MODAL_WINDOW_TYPE.TRANSACTION_CATEGORY_INCOME]:
        AddTransactionCategoryForm,
      [NON_MODAL_WINDOW_TYPE.EDIT_INCOME]: EditTransactionCategoryForm,
      [NON_MODAL_WINDOW_TYPE.EDIT_EXPENSE]: EditTransactionCategoryForm,
      [NON_MODAL_WINDOW_TYPE.EDIT_VAULT]: EditVaultForm,
      [NON_MODAL_WINDOW_TYPE.EDIT_GROUP]: EditGroupForm,
      [NON_MODAL_WINDOW_TYPE.MODELS]: Models,
      [NON_MODAL_WINDOW_TYPE.MODELS_WINDOW]: ModelsWindow,
    }

    const Comp = componentMap[nonModalWindowType]
    const key = nonModalProps?.componentKey ?? undefined
    return Comp ? <Comp {...nonModalProps} key={key} /> : null
  }, [nonModalWindowType, nonModalProps])

  const currentCell = nonModalProps?.currentCell as Cell<unknown, unknown>

  return { open, PortalComponent, currentCell, updateExistingWindow }
}
